// Fire a callback when a scroll container is near one of its edges — the "load the next page by
// scrolling" half of both paged surfaces on this page.
//
// The two surfaces scroll in OPPOSITE directions — the rail appends downward, the transcript
// prepends upward — so all they share is "near the edge means fetch". The direction is a parameter;
// what to do with the rows stays with each caller.
//
// ⚠ THE SCROLL CONTAINER IS NEITHER THE VIEWPORT NOR THE COMPONENT. The rail scrolls inside
//   `.railList` and the transcript inside a `.scroll` wrapper that sits ABOVE the component in the
//   tree, so neither can name its root as a ref. It is found by walking up from the sentinel to the
//   first ancestor that actually scrolls.
//
// ★ A SCROLL LISTENER RATHER THAN IntersectionObserver, DELIBERATELY. IO is the usual answer and it
//   is the one this started as. It was replaced because IO reports through the rendering pipeline,
//   which makes it silent wherever that pipeline is throttled — MEASURED in an automated browser
//   here, where `requestAnimationFrame` never fired at all and an observer on a fully visible
//   sentinel produced no callback in 2s. A scroll handler plus an explicit re-check depends on
//   nothing but the scroll position, so it behaves the same in a driven browser as in a real one —
//   and it is what makes this testable rather than merely plausible.
//
// ★ IT ALSO CHECKS WITHOUT A SCROLL EVENT, which is not an optimisation but a correctness case: a
//   page shorter than its container never scrolls, so a reader would have no gesture available to
//   ask for the next one and the list would stop early with more to give.

import { useCallback, useEffect, useRef } from "react";

export function scrollParent(el) {
  let node = el && el.parentElement;
  while (node) {
    const style = window.getComputedStyle(node);
    if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * @param {object}   opts
 * @param {boolean}  opts.enabled    false detaches entirely — an exhausted list keeps no listener,
 *                                   and a fetch in flight cannot start a second.
 * @param {Function} opts.onReach    called when the edge is within `threshold`.
 * @param {"top"|"bottom"} opts.edge which edge to watch.
 * @param {number}   opts.threshold  pixels from the edge; the fetch starts before it is reached.
 * @returns {{ sentinelRef: object, scrollRef: object }} — `scrollRef` is exposed because a caller
 *          that PREPENDS must measure the container itself to keep the reader in place.
 */
export function useNearEdge({ enabled, onReach, edge = "bottom", threshold = 300 }) {
  const sentinelRef = useRef(null);
  const scrollRef = useRef(null);
  // Held in a ref so a changing callback identity does not detach and reattach the listener.
  const onReachRef = useRef(onReach);
  onReachRef.current = onReach;

  const check = useCallback(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    const near =
      edge === "top"
        ? sc.scrollTop <= threshold
        : sc.scrollHeight - sc.scrollTop - sc.clientHeight <= threshold;
    if (near) onReachRef.current();
  }, [edge, threshold]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!enabled || !el) return undefined;
    const sc = scrollParent(el);
    scrollRef.current = sc;
    if (!sc) return undefined;
    sc.addEventListener("scroll", check, { passive: true });
    // The re-check above: with `enabled` newly true the reader may ALREADY be at the edge, and no
    // scroll event is coming to tell us so.
    check();
    return () => sc.removeEventListener("scroll", check);
  }, [enabled, check]);

  return { sentinelRef, scrollRef };
}
